import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Preload image URLs so morphing a gallery does not flash alt text / blank boxes.
 * @param {ParentNode} root
 * @param {number} [limit]
 * @returns {Promise<void>}
 */
function preloadGalleryImages(root, limit = 8) {
  const imgs = Array.from(root.querySelectorAll('img.product-media__image, .media-gallery__grid img, slideshow-slide img'));
  const urls = [];
  for (const img of imgs) {
    if (!(img instanceof HTMLImageElement)) continue;
    const src = img.currentSrc || img.getAttribute('src') || '';
    if (!src || src.startsWith('data:')) continue;
    if (urls.includes(src)) continue;
    urls.push(src);
    if (urls.length >= limit) break;
  }

  if (!urls.length) return Promise.resolve();

  return Promise.all(
    urls.map(
      (src) =>
        new Promise((resolve) => {
          const probe = new Image();
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve();
          };
          probe.onload = finish;
          probe.onerror = finish;
          probe.src = src;
          if (probe.complete) finish();
          // Cap wait so a slow CDN never blocks UX forever
          setTimeout(finish, 900);
        })
    )
  ).then(() => undefined);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {number} */
  #updateToken = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Stable key for morphing slides/grid tiles across variant updates.
   * @param {Node | undefined} node
   * @returns {string | number | undefined}
   */
  #mediaNodeKey(node) {
    if (!(node instanceof HTMLElement)) return undefined;

    if (node.dataset.mediaId) return node.dataset.mediaId;
    if (node.dataset.hydrationKey) return node.dataset.hydrationKey;

    const mediaHost = node.matches('[data-media-id], [data-hydration-key]')
      ? node
      : node.querySelector('[data-media-id], [data-hydration-key]');

    if (mediaHost instanceof HTMLElement) {
      return mediaHost.dataset.mediaId || mediaHost.dataset.hydrationKey;
    }

    const slideId = node.getAttribute('slide-id');
    if (slideId) return slideId;

    return undefined;
  }

  /**
   * Featured media id currently shown first in the gallery.
   * @returns {string | undefined}
   */
  #currentFeaturedMediaId() {
    const first =
      this.querySelector('.media-gallery__grid [data-media-id]') ||
      this.querySelector('slideshow-slide [data-media-id]') ||
      this.querySelector('[data-media-id]');

    return first instanceof HTMLElement ? first.dataset.mediaId : undefined;
  }

  /**
   * Handles a variant update event by morphing the gallery in-place.
   * Skips work when featured media is unchanged (size-only changes).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const html = event.detail.data.html;
    if (!html) return;

    const newMediaGallery = html.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const nextFeaturedId = event.detail.resource?.featured_media?.id?.toString();
    const currentFeaturedId = this.#currentFeaturedMediaId();

    // Size-only change, or main was already swapped to this product — keep painted gallery.
    if (nextFeaturedId && currentFeaturedId && nextFeaturedId === currentFeaturedId) {
      return;
    }

    // Combined-listing color switch already replaced <main>; gallery is fresh — don't remorph.
    if (event.detail.data.newProduct) {
      return;
    }

    const token = ++this.#updateToken;
    const slideshow = this.slideshow;
    const previousIndex = slideshow?.current ?? 0;

    this.classList.add('media-gallery--updating');

    await preloadGalleryImages(newMediaGallery, 8);
    if (token !== this.#updateToken || !this.isConnected) return;

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => this.#mediaNodeKey(node),
      reject(oldNode, newNode) {
        if (MORPH_OPTIONS.reject?.(oldNode, newNode)) return true;

        // Keep already-loaded <img> nodes when src is identical — stops white flash.
        if (
          oldNode instanceof HTMLImageElement &&
          newNode instanceof HTMLImageElement &&
          oldNode.src &&
          oldNode.src === newNode.src
        ) {
          return true;
        }

        return false;
      },
      onAfterUpdate: (node) => {
        MORPH_OPTIONS.onAfterUpdate?.(node);
      },
    });

    queueMicrotask(() => {
      this.classList.remove('media-gallery--updating');

      const nextSlideshow = this.slideshow;
      if (!nextSlideshow) return;

      // Morph can drop viewport scroll enablement — force swipe back on.
      nextSlideshow.setAttribute('in-viewport', '');
      const slides = nextSlideshow.querySelector('slideshow-slides');
      if (slides instanceof HTMLElement) {
        slides.style.overflowX = 'auto';
        slides.style.touchAction = 'pan-x pan-y pinch-zoom';
      }

      const targetIndex = nextFeaturedId ? 0 : Math.min(previousIndex, (nextSlideshow.slides?.length ?? 1) - 1);
      nextSlideshow.select(targetIndex, undefined, { animate: false });

      if (typeof window.__ashVideoLazySync === 'function') {
        window.__ashVideoLazySync();
      }
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

// Expose for variant-picker combined-listing main morph
window.__ashPreloadGalleryImages = preloadGalleryImages;
